import { useCallback, useEffect, useMemo, useRef } from 'react';
import styled, { keyframes } from 'styled-components';

import { ApiNotification } from 'api/notification/types/ApiNotification';
import { Icon } from 'components/Icon';
import { useIsClient } from 'hooks/useIsClient';
import { colors } from 'theme/theme';
import { MappedNotification } from 'types/mappedNotifications/MappedNotification';
import { isBrowser } from 'utils/browser';
import { getItem, setItem } from 'utils/localStorage';

const MAX_NOTIFICATION_COUNT = 99;

const animation = keyframes`
   0% { rotate: 0; }
  1% { rotate: 30deg; }
  3% { rotate: -28deg; }
  5% { rotate: 34deg; }
  7% { rotate: -32deg; }
  9% { rotate: 30deg; }
  11% { rotate: -28deg; }
  13% { rotate: 26deg; }
  15% { rotate: -24deg; }
  17% { rotate: 22deg; }
  19% { rotate: -20deg; }
  21% { rotate: 18deg; }
  23% { rotate: -16deg; }
  25% { rotate: 14deg; }
  27% { rotate: -12deg; }
  29% { rotate: 10deg; }
  31% { rotate: -8deg; }
  33% { rotate: 6deg; }
  35% { rotate: -4deg; }
  37% { rotate: 2deg; }
  39% { rotate: -1deg; }
  41% { rotate: 1deg; }
  43% { rotate: 0; }
  100% { rotate: 0; }
}
`;

const Tag = styled.div`
  z-index: 1;
  position: absolute;
  right: 0;
  top: 0;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: ${colors.decorativePoppy};
  font-size: 12px;
  color: ${colors.white};
  text-align: center;
`;

const Wiggle = styled.div`
  margin-right: 4px;

  &.animate {
    animation: ${animation} 5s ease-in-out forwards;
  }
`;

const Container = styled.div`
  position: relative;
  width: fit-content;
`;

type Props = {
  id: string;
  notifications: (ApiNotification | MappedNotification)[];
};

export function NotificationsIcon(props: Props) {
  const { notifications, id } = props;

  const isClient = useIsClient();

  const wiggleRef = useRef<HTMLDivElement>(null);

  const unseenNotifications = useMemo(
    () => notifications.filter((notification) => !notification.viewed),
    [notifications],
  );

  const showBadge = useMemo(
    () => isBrowser() && unseenNotifications.length > 0,
    [unseenNotifications],
  );

  const handleAnimationEnd = useCallback(() => {
    wiggleRef.current?.classList.remove('animate');
  }, []);

  useEffect(() => {
    const previousLocalNotificationLength = getItem(
      `unseenNotificationLength-${id}`,
    );
    const currentNotificationLength = unseenNotifications.length;

    // Compare the current to the previously unseen notifications
    // and determine wether or not to animate the notifications bell.
    if (
      wiggleRef.current &&
      currentNotificationLength !== 0 &&
      previousLocalNotificationLength !== currentNotificationLength
    ) {
      wiggleRef.current?.classList.add('animate');

      setItem(`unseenNotificationLength-${id}`, unseenNotifications.length);
    }
  }, [unseenNotifications.length, id]);

  if (!isClient) return null;

  return (
    <Container id={id}>
      {showBadge ? (
        <Tag>
          {unseenNotifications.length > MAX_NOTIFICATION_COUNT ? (
            <>&infin;</>
          ) : (
            unseenNotifications.length
          )}
        </Tag>
      ) : null}

      <Wiggle ref={wiggleRef} onAnimationEnd={handleAnimationEnd}>
        <Icon name="bell-2" size={22} />
      </Wiggle>
    </Container>
  );
}
